import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import cn from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'

import Logo from '../Logo'
import HeaderContactOptionsMenu from '../HeaderContactOptions/HeaderContactOptionsMenu'
import CookiesDisabledWarningBanner from '../HeaderBanners/CookiesDisabledWarningBanner'
import NotificationBanner from '../HeaderBanners/NotificationBanner'
import ScrollLock from 'components/Common/ScrollLock'
import { isMobileSearchEnabled, isSearchEnabled } from 'lib/searchUtils'
import { useHeaderHeightObserver } from 'contexts/HeaderHeightContext'

import {
  closeMainNav,
  openMainNav,
} from 'actions/UiActions'

import config from 'constants/config'
import { connect } from 'react-redux'
import zIndex from 'styles/tools/z-index'
import NavigationHeader from './NavigationHeader/NavigationHeader'
import NavigationMenu from './NavigationMenu/NavigationMenu'
import LayoutContainer from 'components/Common/LayoutContainer'

import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import IconButton from 'components/Luxkit/Button/IconButton'
import { useResponsive } from 'hooks/useResponsive'
import CSSBreakpoint from 'components/Common/CSSBreakpoint'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import NavigationHeaderTripsMenu from './NavigationHeader/NavigationHeaderTripsMenu'
import ContactOptionsBanner from '../HeaderBanners/ContactOptionsBanner'
import NavigationNavItems from './NavigationHeader/NavigationNavItems'
import LineTimesIcon from 'components/Luxkit/Icons/line/LineTimesIcon'
import LineBarsIcon from 'components/Luxkit/Icons/line/LineBarsIcon'
import HeaderUserAvatar from './HeaderUserAvatar'
import SearchToggle from './SearchToggle'
import NavigationTertiaryHeader from './NavigationHeader/NavigationTertiaryHeader'
import BusinessTravellerAccountIconButtonMenu from 'businessTraveller/components/business-account/BusinessTravellerAccountIconButtonMenu'
import LineSearchIcon from 'components/Luxkit/Icons/line/LineSearchIcon'
import { searchNavCTAClick } from 'analytics/eventDefinitions'
import { addGTMEvent } from 'api/googleTagManager'
import useCurrentGlobalSearchVerticalPage from 'hooks/GlobalSearch/useCurrentGlobalSearchVerticalPage'
import useGlobalSearchURLHashVertical from 'hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import useGlobalSearchVerticalTabs from 'hooks/GlobalSearch/useGlobalSearchVerticalTabs'
import { Location } from 'history'
import { isLoggedIn } from 'selectors/accountSelectors'
import AtolProtectedBanner from '../HeaderBanners/AtolProtectedBanner'
import { isMobileUkAtolBannerEnabled } from 'selectors/featuresSelectors'
import { useStickyMainHeader } from 'selectors/stickyMainHeader'

const wlMobileAppStyle = config.MOBILE_APP_CUSTOMISATIONS ? `padding-top: ${rem(50)};` : ''

const Root = styled.header`
  position: relative;
  background: ${({ theme }) => theme.palette.neutral.eight};
  display: flex;
  flex-direction: column;

  &.open {
    z-index: ${zIndex.header};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    ${mediaQueryUp.navbarFixed} {
      position: relative;
    }
  }

  &.useStickyMainHeader:not(.open) {
    position: sticky;
    top: 0;
    z-index: ${zIndex.stickyHeader};
  }
`

const MainHeader = styled.div`
  display: grid;
  align-items: center;
  background-color: ${({ theme }) => theme.palette.neutral.eight};
  height: ${rem(72)};
  z-index: 2;
  grid-template-columns: auto auto 1fr;
  grid-column-gap: ${rem(16)};

  ${mediaQueryUp.xs} {
    grid-template-columns: auto 1fr auto;

    &.search-enabled {
      grid-template-columns: auto auto 1fr auto;
    }
  }

  ${mediaQueryUp.navbarFixed} {
    grid-column-gap: ${rem(20)};
    grid-template-columns: 1fr auto;

    &.search-enabled {
      grid-template-columns: auto 1fr auto;
    }
  }
`

const HeaderLogo = styled(Logo)`
  ${mediaQueryDown.xs} {
    flex-grow: 1;
    ${config.BRAND !== 'luxuryescapes' ? 'min-height: 35px;' : ''}
  }
`

const MobileHeaderItems = styled.div`
  display: flex;
  align-items: center;
  justify-self: flex-end;
  gap: ${rem(8)};

  ${mediaQueryUp.sm} {
    display: none;
  }
`

const DesktopNavigationHeader = styled(NavigationHeader)`
  display: none;

  ${mediaQueryUp.sm} {
    display: flex;
  }
`

const MobileNavigationMenu = styled(NavigationMenu)`
  display: none;
  flex-grow: 1;

  &.open {
    display: block;

    ${mediaQueryUp.navbarFixed} {
      position: absolute;
      top: ${rem(72)};
      height: calc(100vh - ${rem(72)});
      overflow: auto;
      width: ${rem(400)};
    }
  }

  ${mediaQueryUp.navbarFixed} {
     display: none;
  }
`

const MobileNavMenuToggle = styled(IconButton)`
  /* Negative margin-right to offset the button's padding, so that the grid gap remains visually consistent */
  margin-right: ${rem(-16)};

  ${mediaQueryUp.navbarFixed} {
     display: none;
  }
`

const HeaderContainer = styled(LayoutContainer)`
  position: relative;
  ${wlMobileAppStyle}
`

const NavigationSubHeaderWrapper = styled.div`
  background: ${({ theme }) => theme.palette.neutral.eight};
  border-bottom: 1px solid ${({ theme }) => theme.palette.neutral.five};
  box-shadow: ${({ theme }) => theme.shadow.flat.small};
`

const NavigationSubHeader = styled(LayoutContainer)`
  background: ${({ theme }) => theme.palette.neutral.eight};

  &.open {
    display: none;
  }
`

const StickyContactOptionsBanner = styled(ContactOptionsBanner)`
  position: sticky;
  top: 0;
  z-index: 4;

  ${mediaQueryUp.sm} {
    display: none;
  }
`

const AtolProtectedBannerWrapper = styled(AtolProtectedBanner)`
  ${mediaQueryUp.sm} {
    display: none;
  }
`

const DesktopSearchBarContainer = styled.div`
  width: ${rem(320)};
  margin: 0 auto;

  ${mediaQueryUp.md} {
    width: auto;
    max-width: ${rem(440)};
  }
`

const MobileSearchBarContainer = styled.div`
  padding: ${rem(4)} 0 ${rem(12)};

  &.open {
    display: none;
  }
`

interface MappedProps {
  isLoggedIn: boolean
  open: boolean
  useStickyMainHeader: boolean
  location: Location
  isMobileUkAtolABTestEnabled: boolean
}

interface Props {
  className?: string
  notificationSlug?: string
  closeMainNav: () => void
  openMainNav: () => void
}

const IS_DESKTOP_SEARCH_BAR_ENABLED = isSearchEnabled() && config.businessTraveller.currentAccountMode !== 'business'
const IS_MOBILE_SEARCH_BAR_ENABLED = isMobileSearchEnabled() && config.businessTraveller.currentAccountMode !== 'business'

function HeaderMain(props: Props & MappedProps) {
  const {
    className,
    isLoggedIn,
    notificationSlug,
    open,
    closeMainNav,
    openMainNav,
    useStickyMainHeader,
    location,
    isMobileUkAtolABTestEnabled,
  } = props

  const [scrolled, setScrolled] = useState<boolean>(false)
  const handleScroll = useCallback(
    () => setScrolled(useStickyMainHeader && window.scrollY > 0),
    [useStickyMainHeader],
  )

  const [match] = useResponsive()
  const navbarFixed = useMemo(() => match.navbarFixed, [match])

  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()
  const currentSearchVerticalPage = useCurrentGlobalSearchVerticalPage()
  const globalSearchVerticalTabs = useGlobalSearchVerticalTabs()

  const handleSearchButtonClick = useCallback(() => {
    if (!currentSearchVerticalPage) {
      toggleSearchVertical([...globalSearchVerticalTabs.keys()][0])
    } else {
      toggleSearchVertical(currentSearchVerticalPage)
    }
    addGTMEvent(searchNavCTAClick())
  }, [currentSearchVerticalPage, globalSearchVerticalTabs, toggleSearchVertical])

  useEffect(() => {
    if (open && navbarFixed) {
      closeMainNav()
    }
  }, [closeMainNav, open, navbarFixed])

  const toggle = useCallback(() => {
    if (open) {
      closeMainNav()
    } else {
      openMainNav()
    }
  }, [open, closeMainNav, openMainNav])

  useEffect(() => {
    handleScroll()
    document.removeEventListener('scroll', handleScroll)

    if (useStickyMainHeader) {
      document.addEventListener('scroll', handleScroll)
      return () => {
        document.removeEventListener('scroll', handleScroll)
      }
    }
  }, [handleScroll, useStickyMainHeader])

  const rootRef = useRef<HTMLElement>(null)
  useHeaderHeightObserver('site', rootRef)
  const stickyBannerRef = useRef<HTMLDivElement>(null)
  useHeaderHeightObserver('siteStickyBanner', stickyBannerRef)

  const shouldReduceSearchBarToIcon = location.pathname.endsWith('/search')

  return <>
    <TripGuard>
      {config.SHOW_CONTACT_BANNER && <StickyContactOptionsBanner ref={stickyBannerRef} />}
    </TripGuard>
    {isMobileUkAtolABTestEnabled && <AtolProtectedBannerWrapper />}
    <Root ref={rootRef} className={cn('PageHeader', className, { open, useStickyMainHeader, scrolled })}>
      {open && <TripGuard>
        {config.SHOW_CONTACT_BANNER && <CSSBreakpoint as={ContactOptionsBanner} max="sm" />}
        {isMobileUkAtolABTestEnabled && <AtolProtectedBannerWrapper />}
      </TripGuard>}
      {config.notification && <NotificationBanner slug={notificationSlug} />}
      <CookiesDisabledWarningBanner />
      <CSSBreakpoint min="sm">
        <NavigationTertiaryHeader />
      </CSSBreakpoint>
      <HeaderContainer>
        <MainHeader className={cn({
          'search-enabled': IS_DESKTOP_SEARCH_BAR_ENABLED,
        })}>
          <MobileNavMenuToggle
            kind="tertiary"
            onClick={toggle}
            aria-expanded={open}
            size="large"
            variant="dark"
            outdent="left"
            aria-label={open ? 'Close main menu' : 'Open main menu'}
            className={cn(className, { open })}
          >
            {open ? <LineTimesIcon /> : <LineBarsIcon />}
          </MobileNavMenuToggle>
          <HeaderLogo />
          {IS_DESKTOP_SEARCH_BAR_ENABLED && (
            <CSSBreakpoint min="sm">
              <DesktopSearchBarContainer>
                <SearchToggle />
              </DesktopSearchBarContainer>
            </CSSBreakpoint>
          )}
          <MobileHeaderItems>
            <TripGuard>
              <NavigationHeaderTripsMenu variant="mobile"/>
            </TripGuard>
            <TripGuard inverse>
              <HeaderContactOptionsMenu />
            </TripGuard>
            {shouldReduceSearchBarToIcon && <IconButton
              kind="primary"
              shape="circle"
              className={className}
              onClick={handleSearchButtonClick}
            >
              <LineSearchIcon />
            </IconButton>}
            {config.businessTraveller.isEnabled && <BusinessTravellerAccountIconButtonMenu />}
            {config.header.standAloneAccount && !config.businessTraveller.isEnabled &&
              <HeaderUserAvatar />}
          </MobileHeaderItems>
          <DesktopNavigationHeader
            isLoggedIn={isLoggedIn}
          />
        </MainHeader>
        {IS_MOBILE_SEARCH_BAR_ENABLED && !shouldReduceSearchBarToIcon && (
          <CSSBreakpoint max="xs">
            <MobileSearchBarContainer className={cn({ open })}>
              <SearchToggle />
            </MobileSearchBarContainer>
          </CSSBreakpoint>
        )}
      </HeaderContainer>
      <MobileNavigationMenu className={cn({ open })} />
    </Root>
    <NavigationSubHeaderWrapper>
      <NavigationSubHeader className={cn({ open })}>
        <NavigationNavItems />
      </NavigationSubHeader>
    </NavigationSubHeaderWrapper>
    <ScrollLock isActive={open} />
  </>
}

export default connect((state: App.State): MappedProps => ({
  open: state.ui.mainNav.open,
  isLoggedIn: isLoggedIn(state),
  useStickyMainHeader: useStickyMainHeader(state),
  location: state.router.location,
  isMobileUkAtolABTestEnabled: isMobileUkAtolBannerEnabled(state),
}), {
  openMainNav,
  closeMainNav,
})(HeaderMain)
